
import { useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Button } from "@/components/ui/button";
import DynamicTitle from '../components/DynamicTitle';
import SEOStructuredData from '../components/SEOStructuredData';

const UpworkJourney = () => {
  useEffect(() => {
    console.log("User visited Upwork Journey page");
  }, []);

  return (
    <div className="min-h-screen bg-black">
      <DynamicTitle 
        title="My Upwork Journey - Finding My Way as a Freelancer in a New Country"
        description="Aram AI's complete Upwork freelancing journey from $5/hr to $30/hr+. Learn about building a successful freelance career and growing professionally."
        keywords="Upwork freelancing, freelancer journey, Spain freelance work, remote work, freelance career growth, digital nomad"
        canonicalUrl="https://aramb.am/upwork-journey"
      />
      <SEOStructuredData 
        type="article"
        title="My Upwork Journey: Finding My Way as a Freelancer in a New Country"
        description="The complete story of building a successful freelance career on Upwork from scratch in a new country."
        url="https://aramb.am/upwork-journey"
        image="https://aramb.am/lovable-uploads/a3b94ebd-5632-4be3-bdc3-d28bbdb0eb5a.png"
        author="Aram AI"
        datePublished="2024-12-17"
      />
      <Header />
      
      <main className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 font-heading glow-text">
            My Upwork Journey: Finding My Way as a Freelancer in a New Country
          </h1>
        </div>

        {/* Hero Image */}
        <div className="mb-12">
          <AspectRatio ratio={16/9} className="overflow-hidden rounded-lg">
            <img 
              src="/lovable-uploads/a3b94ebd-5632-4be3-bdc3-d28bbdb0eb5a.png" 
              alt="Upwork profile progression" 
              className="w-full h-full object-cover"
            />
          </AspectRatio>
          <p className="text-center text-sm text-gray-400 mt-3 italic">
            My progression on Upwork: starting at $5/hr, growing to consistent $30/hr+ rates with 100% job success. This journey represents thousands of hours of dedication after arriving with nothing.
          </p>
        </div>

        {/* Article Content */}
        <div className="max-w-4xl mx-auto mb-24">
          <article className="glass-panel rounded-2xl overflow-hidden">
            <div className="p-12 space-y-8">
              <div className="prose prose-invert prose-xl max-w-none leading-relaxed">
                <p className="text-xl leading-[1.8] text-gray-200 font-light mb-8 first-letter:text-6xl first-letter:font-bold first-letter:text-purple-300 first-letter:float-left first-letter:mr-3 first-letter:mt-1">
                  Landing in Spain in January 2018 was a fresh start, but also a dive into the unknown. For the first eight months, I was figuring things out. New country, new language, no documents yet, no friends nearby – it was a lot to take in at 21. One thing was crystal clear though: a typical 9-to-5 job wasn't what I wanted, and honestly, without residency at that point, it wasn't even an option.
                </p>

                <p className="text-lg leading-[1.8] text-gray-300 mb-6">
                  My life before Spain had been a mix of very different things. I was building websites when I was just 10, got into music around 16, worked as a sports live trader, and even drove a taxi for a while. I had a range of experiences, but no single "specialty" I could easily name. Now, it was time to figure out how to make a living.
                </p>

                <div className="bg-purple-900/20 border-l-4 border-purple-400 pl-6 py-4 my-8 rounded-r-lg">
                  <h2 className="text-2xl font-bold text-purple-200 mb-4">The Nudge That Changed Things</h2>
                  <p className="text-lg leading-[1.8] text-gray-200">
                    After about six months of being completely broke and searching for opportunities, a friend visited Spain with his family. We met up, and he told me about this platform called Upwork, a place to find all sorts of online work. I'd heard of it before but hadn't taken it seriously. Why? Because I didn't see myself as a "developer," "designer," or "accountant." I didn't have a neat label for what I could offer. But when he mentioned he knew people actually earning real money there, I thought, "Okay, why not give it a try?"
                  </p>
                </div>

                <p className="text-lg leading-[1.8] text-gray-300 mb-6">
                  So, I signed up and started applying for proposals. I had no clear idea what I was doing or even what services I could really offer the world. Translations? Admin tasks? Basic web development? I decided to try a bit of everything.
                </p>

                <h2 className="text-3xl font-bold mb-6 text-white">My First Gig and the Freelance Adventure Begins</h2>

                <p className="text-lg leading-[1.8] text-gray-300 mb-6">
                  Amazingly, after about a week of sending out proposals, I got some interest, then an actual offer! That's how my freelance journey kicked off. My very first contract was with an Indian AR/VR company. Their team was in Barcelona for a big gas tech event, and they needed someone local to help with organizational tasks. It turned out to be a really fun experience. I was at this huge event, helping these guys out, and even got to play games, demoing their VR/AR software.
                </p>

                <p className="text-lg leading-[1.8] text-gray-300 mb-6">
                  After that first taste, freelancing started to feel exciting. I began working with clients from all over the globe – the US, UK, New Zealand, Hong Kong, Australia, Japan, various EU countries, Canada, Israel. It was a fantastic experience, tackling different projects with diverse tasks for each client. I was doing okay, but it wasn't stable. One month I might make €800, the next €1200, and then maybe only €400.
                </p>

                <p className="text-lg leading-[1.8] text-gray-300 mb-6">
                  I knew I couldn't really push my rates up significantly at that point for two main reasons:
                </p>

                <ul className="list-disc pl-6 mb-6 text-lg leading-[1.8] text-gray-300 space-y-2">
                  <li>I wasn't offering highly specialized or extraordinary skills.</li>
                  <li>I was mostly working on small, one-time projects.</li>
                </ul>

                <p className="text-lg leading-[1.8] text-gray-300 mb-6">
                  I realized I needed to look for more stable, longer-term opportunities.
                </p>

                <h2 className="text-3xl font-bold mb-6 text-white">Finding More Solid Ground</h2>

                <p className="text-lg leading-[1.8] text-gray-300 mb-6">
                  My search led me to a part-time project with a Texas-based startup. They were building a platform for learning languages through music, and my role was to contact artists and manage outreach. I worked with them for several months.
                </p>

                <p className="text-lg leading-[1.8] text-gray-300 mb-6">
                  After that project wrapped up, I found a job posting looking for someone in Valencia for admin tasks. I applied, got a response, and this time it led to an in-person meeting. The team was from Israel, and they were helping Israeli citizens apply for Portuguese citizenship. From the first moment, these guys were great – fun, open, and straightforward. We met in a restaurant, had some food and a beer, and by the end of it, they'd decided to hire me.
                </p>

                <div className="text-center my-10">
                  <div className="inline-block bg-gradient-to-r from-green-600/20 to-blue-600/20 p-8 rounded-xl border border-green-500/30">
                    <p className="text-2xl font-semibold text-green-200 mb-4">Career Growth</p>
                    <p className="text-lg text-gray-300">Started at <span className="font-bold text-white">$11/hr</span> with the Israeli team</p>
                    <p className="text-sm text-gray-400 mt-2">A stable 1.5-year freelancing partnership</p>
                  </div>
                </div>

                <p className="text-lg leading-[1.8] text-gray-300 mb-6">
                  We quickly became good friends. I trained with them for a week, and then we hit the ground running. This turned into a very good 1.5 years of full-time freelancing. By the time I started with them, my general hourly rate had also increased, and I began at $11/hr.
                </p>

                <h2 className="text-3xl font-bold mb-6 text-white">A Stepping Stone to What's Next</h2>

                <p className="text-lg leading-[1.8] text-gray-300 mb-6">
                  Those 1.5 years were crucial. While working full-time with the Israeli team, I was also able to sort out my documentation in Spain and build a more stable life. With that stability came the headspace to think about the future. I was already researching different business ideas because I always saw entrepreneurship as my path forward. It was during this period that I learned about Amazon FBA businesses.
                </p>

                <p className="text-lg leading-[1.8] text-gray-300 mb-6">
                  In my last few months working with them, I started diving deep into learning everything I could about Amazon FBA. That's a whole story in itself, which I've detailed on my Amazon FBA Experience page.
                </p>

                <div className="bg-black/40 border border-purple-500/30 rounded-xl p-8 my-10">
                  <p className="text-lg leading-[1.8] text-gray-300 mb-6">
                    My Upwork story doesn't entirely end here, as I did return to it later in connection with my Amazon FBA journey, but that's a chapter for another time. For now, this covers my foundational experience on the platform – a period of immense learning, adaptation, and discovering what I was truly capable of in a new environment.
                  </p>
                  
                  <div className="text-center mt-8">
                    <Link to="/projects/amazon">
                      <Button className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded-lg font-medium transition-colors">
                        My Amazon FBA Journey
                      </Button>
                    </Link>
                  </div>
                </div>

                <p className="text-xl leading-[1.8] text-gray-200 font-medium">
                  Upwork was a critical launchpad for me – teaching me resilience, adaptability, and the value of building genuine relationships with clients across the globe.
                </p>
              </div>
            </div>
          </article>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default UpworkJourney;
